
/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback, Suspense, lazy, useRef } from "react";
import axios from 'axios';
import { store } from "../redux/store";
import { setFlowData } from "../redux/slices/flowSlice";
import GlobalConfig from "../context/GlobalConfig";
import JourneyConfig from "../context/JourneyConfig";
import VerificationTerminated from './../containers/VerificationTerminated';
import VerificationComplete from './../containers/VerificationComplete';
import useSessionId from "../hooks/useSessionId";
import Loader from "../components/Loader/Loader";
import Base from '../Base';
import ErrorBoundary from '../components/ErrorBoundary';
import useDeviceDetection from "../hooks/useDeviceDetection";
import useTabVisibilityRefresh from "../hooks/useTabVisibilityRefresh";
import JourneyStopped from "./JourneyStopped";
import Expired from "../components/Expired";
import { createOrchestration } from "../utils/orchestrationModule";
import { setHyperDXContext } from "../utils/hyperdx";

// const API_BASE_URL = "https://kyc-preproduction.signzy.app/otk-service"
// const API_BASE_URL = "http://api.prv:51266/otk-service"
// const API_BASE_URL = "https://kyc-staging.signzy.app/otk-service"


// Index of the FIRST document's unable-to-process route in the given route map. The NO_DOC and
// DOC route arrays number their pages differently, so this has to be recomputed every time the
// orchestration is rebuilt — a value carried over from the previous array points at the wrong page.
// Object.entries keys are strings; return a number so downstream arithmetic doesn't string-concat.
const findUnableToProcessStep = (routes) => {
  const firstMatchingIndex = Object.entries(routes ?? {}).find(([_, path]) =>
    path.includes("unable-to-process")
  )?.[0];
  return firstMatchingIndex === undefined ? -1 : Number(firstMatchingIndex);
};

const Settings = () => {
  const url = window.location.href;
  const urlParts = url.split(/[/?]/);
  const extractedJourneyID =
    (urlParts.length >= 4 ? urlParts[3] : "") ?? sessionStorage.getItem("jId");
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [isConfigReady, setIsConfigReady] = useState(false);
  const [config, setConfig] = useState({});
  const [routesAndNavigation, setRoutesAndNavigation] = useState({});
  const [routesAndNavigationLoaded, setRoutesAndNavigationLoaded] = useState(false);
  const routesAndNavigationLoadedRef = useRef(null);
  const [journeyInitialized, setJourneyInitialized] = useState(-1);
  const [journey, setJourney] = useState(null);
  const [currentStepFromBackend, setCurrentStepFromBackend] = useState(0);
  const [journeyId, setJourneyId] = useState(extractedJourneyID);
  const [isInvalidJourneyId, setIsInvalidJourneyId] = useState(extractedJourneyID ? false : true);
  const [isDirectKycJourney, setIsDirectKycJourney] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [mobileSessionDone, setMobileSessionDone] = useState(false);
  const [journeyTerminated, setJourneyTerminated] = useState(false);
  const [favicon, setFavicon] = useState("");
  const [flowRegion, setFlowRegion] = useState("");
  const [journeyExpired, setJourneyExpired] = useState(false);
  const [currentJourneyState, setCurrentJourneyState] = useState({});
  const [journeyDone, setJourneyDone] = useState(false);
  const [sessionId, ...rest] = useSessionId(
    journeyId,
    setJourneyInitialized,
    setJourneyTerminated,
    setJourneyExpired,
    setSessionExpired,
    setJourneyDone,
    setIsInvalidJourneyId,
    setIsDirectKycJourney,
    setError
  );
  const [documentFailureReason, setDocumentFailureReason] = useState("");
  const [isFirstDocumentVerificationDone, setIsFirstDocumentVerificationDone] = useState(false);
  const [unableToProcessStepNumber, setUnableToProcessStepNumber] = useState(-1);
  const [firstVerifiedDocumentName, setFirstVerifiedDocumentName] = useState("")
  const [flowType, setFlowType] = useState("");
  const isMobile = useDeviceDetection();
  const [isPostReloadLoading, setIsPostReloadLoading] = useState(() => {
    // Initialize based on whether isPostReload flag exists in sessionStorage
    return sessionStorage.getItem('isPostReload') === 'true';
  });
  const [flowData, setFlowData] = useState(null);
  const [journeyData, setJourneyData] = useState(null);
  // Initialize tab visibility refresh hook
  // This will automatically refresh the page when user returns to tab on specific routes
  // See TAB_VISIBILITY_REFRESH.md for configuration details
  useTabVisibilityRefresh();

  // updateJourney is memoized on [journeyId, isMobile, flowData], so the `journey` it closes over
  // can be stale. A ref always reflects the latest journey without re-creating that callback.
  const journeyRef = useRef(null);
  journeyRef.current = journey;

  /**
   * How each document is being verified, in document order, for createOrchestration.
   *
   * Documents already captured are read from the journey's capturedData — the backend writes
   * verificationType / verificationType2 / verificationType3 on every DOC_SELECTION. The document
   * being selected right now exists only in this request, so it is layered on top.
   *
   * This used to be built as two positional arguments (document 1's type, and "the one just
   * selected"), which was correct only while a journey captured two documents: on a third
   * document it mapped that document's choice onto document 2, so choosing Digilocker for
   * document 3 silently rerouted document 2.
   *
   * @param {number} selectedDocumentStep - 1-based step of the document being selected
   * @param {string|undefined} selectedVerificationType - 'NO_DOC' when chosen, otherwise undefined
   * @param {string|undefined} firstDocumentVerificationType - document 1's type, per the backend
   */
  const buildVerificationTypes = useCallback(
    (selectedDocumentStep, selectedVerificationType, firstDocumentVerificationType) => {
      const capturedData = journeyRef.current?.capturedData ?? {};
      const verificationTypes = [
        firstDocumentVerificationType ?? capturedData.verificationType,
        capturedData.verificationType2,
        capturedData.verificationType3,
      ];

      if (selectedDocumentStep >= 1) {
        verificationTypes[selectedDocumentStep - 1] = selectedVerificationType;
      }

      return verificationTypes;
    },
    []
  );

  const updateJourney = useCallback(
    async (status, journeyData, currentStep) => {
      try {
        const journeyWithStatus = {
          data: { ...(journeyData || journey) },
          status: status ?? journey?.step,
          journeyId,
          mobileStatus: isMobile ? true : false,
          currentStep: currentStep,
        };
        const {
          data: { result },
        } = await axios.post("/otk-service/update-journey", journeyWithStatus, {
          maxBodyLength: Infinity,
        });

        if (result?.isJourneyDone) {
          sessionStorage.removeItem("sessionId");
          sessionStorage.removeItem("jId");
          sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
          if (result?.journeyDoneReason == "RETRIES_EXCEEDED" || result?.journeyDoneReason == "DUPLICATE_FOUND") {
            if (result?.journeyDoneReason == "DUPLICATE_FOUND" && result?.message) {
              sessionStorage.setItem("terminationMessage", result.message);
            }
            setJourneyTerminated(true);
          } else if (result?.journeyDoneReason == "JOURNEY_EXPIRED") {
            setJourneyExpired(true);
          } else if (result?.journeyDoneReason == "SESSION_TIMED_OUT") {
            setSessionExpired(true);
          } else {
            setJourneyDone(true);
          }
        }
        if (result?.isMobileSessionStopped) {
          setMobileSessionDone(true);
        }
        if (sessionStorage.getItem('error')) {
          sessionStorage.removeItem("error");
        }
        if (status === "DOC_SELECTION" && result?.country === "IN") {
          // Only proceed if flowData is available
          if (flowData?.region && flowData?.flowType && flowData?.config) {
            let orchestrationResult;
            const verificationTypes = buildVerificationTypes(
              journeyData?.currentDocumentStep,
              journeyData?.verificationType,
              result?.firstDocumentVerificationType
            );
            if (journeyData.verificationType === "NO_DOC") {
              orchestrationResult = createOrchestration(flowData?.region, flowData?.flowType, flowData?.config, ...verificationTypes);
              if (orchestrationResult) {
                const { routes, navigation } = orchestrationResult;
                routesAndNavigationLoadedRef.current = false;
                setRoutesAndNavigationLoaded(false); // Reset loading state
                const newRoutesAndNavigation = {
                  routes: routes,
                  navigation: navigation,
                };
                setConfig(prevConfig => ({
                  ...prevConfig,
                  routes: orchestrationResult.routes,
                  navigation: orchestrationResult.navigation
                }));
                setRoutesAndNavigation(newRoutesAndNavigation);
                setUnableToProcessStepNumber(findUnableToProcessStep(routes));
              }
            }
            else {
              orchestrationResult = createOrchestration(flowData?.region, flowData?.flowType, flowData?.config, ...verificationTypes);
              routesAndNavigationLoadedRef.current = false;
              setRoutesAndNavigationLoaded(false); // Reset loading state
              const newRoutesAndNavigation = {
                routes: orchestrationResult.routes,
                navigation: orchestrationResult.navigation,
              };
              setConfig(prevConfig => ({
                ...prevConfig,
                routes: orchestrationResult.routes,
                navigation: orchestrationResult.navigation
              }));
              setRoutesAndNavigation(newRoutesAndNavigation);
              setUnableToProcessStepNumber(findUnableToProcessStep(orchestrationResult.routes));
            }
          } else {
            console.log("Flow data not available yet, skipping orchestration in updateJourney");
          }
        }

        return result;
      } catch (error) {
        let errorDetails = { error:true };
        if (error.response?.data?.error?.error?.statusCode == 403) {
          sessionStorage.removeItem("sessionId");
          sessionStorage.removeItem("jId");
          sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
          if(error?.response?.data?.error?.error?.reason == "DIRECT_KYC_JOURNEY_UPDATE_REQUEST_BLOCKED"){
            setIsDirectKycJourney(true);
          }else if (
            typeof error.response?.data?.error?.error?.error?.message === "string" &&
            error.response?.data?.error?.error?.error?.message.includes("Journey")) {
            setJourneyExpired(true);
          } else {
            setSessionExpired(true);
          }
        }else if(
          typeof error.response?.data?.error?.error?.message === "string" && 
          error.response.data.error.error.message.includes("Invalid journeyId")){
          setIsInvalidJourneyId(true);
        } else {
          sessionStorage.setItem("error", true);
        }
        return errorDetails;
        // else if (journey?.status !== "COMPLETE") setError(error);
      }
    },
    [journeyId, isMobile, flowData]
  );

  const getJourney = useCallback(
    async (jId, stopMobileSession) => {
      try {
        // Client IP is resolved server-side from the X-Forwarded-For header (set
        // by Kong) in one-journey-backend, which is authoritative over any
        // body-supplied value — so the frontend no longer fetches/sends it.
        const isContinuedOnMobile = sessionStorage.getItem("isContinuedOnMobile");
        if (isContinuedOnMobile && !isMobile) setMobileSessionDone(true);
        setJourneyId(jId);
        if (sessionId && (jId === extractedJourneyID || isMobile)) {
          const payload = {
            sessionId,
            isMobile: isMobile ? true : false,
          };
          if (isContinuedOnMobile) {
            payload.stopMobileSession = true;
          }
          const {
            data: { result },
          } = await axios.post("/otk-service/get-session", payload);
          const journeyDataForOrchestration = {
            ...result,
            capturedData: {
              ...result?.capturedData,
              idType: result?.currentJourneyState?.isFirstDocumentVerificationDone
                ? result?.capturedData?.idType2
                : result?.capturedData?.idType,
            }
          };

          setJourney(journeyDataForOrchestration);
          setJourneyData(journeyDataForOrchestration);

          if (result?.deviceContext) {
            sessionStorage.setItem('deviceContext', JSON.stringify(result.deviceContext));
          }
          if (result?.processingConfig) {
            if (result.processingConfig?.failureRedirectUrl != null) {
              sessionStorage.setItem("failureRedirectUrl", result.processingConfig.failureRedirectUrl);
            }
            if (result.processingConfig?.successRedirectUrl != null) {
              sessionStorage.setItem("successRedirectUrl", result.processingConfig.successRedirectUrl);
            }
            if (result.processingConfig?.redirectTime != null) {
              sessionStorage.setItem("redirectTime", result.processingConfig.redirectTime);
            }
            if (result.processingConfig?.language != null && !sessionStorage.getItem('language')) {
              sessionStorage.setItem("language", result.processingConfig.language);
            }
          }
          if (result?.isMobileSessionStopped) {
            setMobileSessionDone(true);
          }
          const documentStep = parseInt(result?.currentJourneyState?.currentDocumentStep, 10);
          const currentDocumentType = documentStep > 0
            ? result?.capturedData?.[`idType${documentStep + 1}`]
            : undefined;
          const documentType = currentDocumentType || (result?.currentJourneyState?.isFirstDocumentVerificationDone
            ? result?.capturedData?.idType2
            : result?.capturedData?.idType);
          sessionStorage.setItem('document', documentType);
          if (result?.isJourneyDone) {
            sessionStorage.removeItem("sessionId");
            sessionStorage.removeItem("jId");
            sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
            if (result?.journeyDoneReason == "RETRIES_EXCEEDED" || result?.journeyDoneReason == "DUPLICATE_FOUND") {
              if (result?.journeyDoneReason == "DUPLICATE_FOUND" && result?.message) {
                sessionStorage.setItem("terminationMessage", result.message);
              }
              setJourneyTerminated(true);
            } else if (result?.journeyDoneReason == "JOURNEY_EXPIRED") {
              setJourneyExpired(true);
            } else if (result?.journeyDoneReason == "SESSION_TIMED_OUT") {
              setSessionExpired(true);
            } else {
              setJourneyDone(true);
            }
          }
          if ("currentJourneyState" in result) {
            setCurrentJourneyState(result?.currentJourneyState);
            setIsFirstDocumentVerificationDone(result?.currentJourneyState?.isFirstDocumentVerificationDone ?? false);
            setFirstVerifiedDocumentName(result?.currentJourneyState?.firstVerifiedDocumentName ?? "")
          }
          if (sessionStorage.getItem('error')) {
            sessionStorage.removeItem("error");
          }
          sessionStorage.removeItem("isContinuedOnMobile");
          return result;
        }
      } catch (error) {
        sessionStorage.removeItem("isContinuedOnMobile");
        if (error.response?.data?.error?.error?.statusCode == 403) {
          sessionStorage.removeItem("sessionId");
          sessionStorage.removeItem("jId");
          sessionStorage.removeItem("routesAndNavigation"); // Clean up routes and navigation
          if(error?.response?.data?.error?.error?.reason == "DIRECT_KYC_JOURNEY_UPDATE_REQUEST_BLOCKED"){
            setIsDirectKycJourney(true);
          }else if (
            typeof error.response?.data?.error?.error?.error?.message === "string" &&
            error.response?.data?.error?.error?.error?.message.includes("Journey")) {
            setJourneyExpired(true);
          } else {
            setSessionExpired(true);
          }
          setError(error);
        } else if(
          typeof error.response?.data?.error?.error?.message === "string" && 
          error.response?.data?.error?.error?.message.includes("Invalid journeyId")){
          setIsInvalidJourneyId(true);
        } else {
          sessionStorage.setItem("error", true);
        }
      }
    },
    [sessionId, isMobile]
  );

  useEffect(() => {
    if (routesAndNavigation.routes && routesAndNavigation.navigation) {
      routesAndNavigationLoadedRef.current = true;
      setRoutesAndNavigationLoaded(true);
    }
  }, [routesAndNavigation]);

  const updateFavicon = (iconUrl) => {
    const link =
      document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = iconUrl;
    document.getElementsByTagName('head')[0].appendChild(link);
  };


  useEffect(() => {
    const branding = sessionStorage.getItem("branding");

    if (favicon || branding) {
      const parsedBranding = branding ? JSON.parse(branding) : null;
      updateFavicon(favicon || parsedBranding?.favicon);
    }
  }, [favicon]);

  useEffect(() => {
    // When config.routes changes (either from getFlow or from updateJourney),
    // mark the config as ready
    if (config.routes && Object.keys(config.routes).length > 0) {
      console.log("✅ Config is ready");
      setIsConfigReady(true);
    }
  }, [config.routes]);

  // Call orchestration when both flow data and journey data are available
  useEffect(() => {
    if (flowData && journeyData) {
      console.log('[Settings] Both flowData and journeyData available, calling createOrchestration');

      // Call createOrchestration with both flow and journey data
      const orchestration = createOrchestration(
        flowData?.region,
        flowData?.flowType,
        flowData?.config,
        journeyData?.capturedData?.verificationType,
        journeyData?.capturedData?.verificationType2,
        journeyData?.capturedData?.verificationType3
      );

      if (orchestration) {
        const { routes, navigation } = orchestration;

        // Update config with new routes and navigation
        setConfig(prevConfig => ({
          ...prevConfig,
          routes: routes,
          navigation: navigation
        }));

        // Update routes and navigation state
        routesAndNavigationLoadedRef.current = true;
        setRoutesAndNavigationLoaded(false);
        const newRoutesAndNavigation = {
          routes: routes,
          navigation: navigation,
        };
        setRoutesAndNavigation(newRoutesAndNavigation);

        console.log('[Settings] Orchestration completed successfully');
        setUnableToProcessStepNumber(findUnableToProcessStep(routes));
      }
    }
  }, [flowData, journeyData]);

  const getFlow = useCallback(async () => {
    try {
      if (journey?.flow?.flowId) {
        const {
          data: { result },
        } = await axios.get(
          `/otk-service/get-active-flow/${journey.flow.flowId}`
        );

        sessionStorage.setItem(
          "branding",
          JSON.stringify(result?.config?.branding)
        );
        if (!sessionStorage.getItem('language')) {
          sessionStorage.setItem('language', result?.config?.branding?.language);
        }
        if (result?.config?.userSettings?.showStatusScreenBeforeRedirection != null) {
          sessionStorage.setItem("showStatusScreenBeforeRedirection", result.config.userSettings.showStatusScreenBeforeRedirection);
        }

        // Store flow data for orchestration
        const flowDataForOrchestration = {
          region: result?.region,
          flowType: result?.flowType,
          config: result?.config
        };
        const config = {
          ...result?.config,
          // Routes and navigation will be generated in frontend
          // Remove dependency on backend routes/navigation
        };

        

        // Note: Unable to process step number will be determined dynamically in frontend
        // based on orchestration logic rather than from backend routes
        // This logic can be moved to Base.jsx after orchestration is created
        setFlowRegion(result?.region);
        setFlowType(result?.flowType);
        setFavicon(result?.config?.branding?.favicon);
        // config.branding (companyLogo + poweredBy) is resolved Flow ▸ Team ▸ Org ▸
        // Signzy by one-journey-backend and arrives inside the flow config, so both
        // the header logo and the footer render partner branding with no extra call.
        setConfig(config);
        // Behavioural-analysis segmentation (authoritative flow attributes).
        setHyperDXContext({
          region: result?.region,
          flowType: result?.flowType,
          flowId: journey?.flow?.flowId,
          language: sessionStorage.getItem("language"),
        });
        // Dispatch flow data to Redux store
        store.dispatch(setFlowData({
          flowId: journey?.flow?.flowId,
          region: result?.region,
          flowType: result?.flowType,
          flowName: result?.flowName,
          config: config,
          clientId: result?.clientId,
          userId: result?.userId,
          fetchedAt: Date.now(),
        }));
      }
    } catch (error) {
      setError(error);
    }
  }, [journey?.flow?.flowId]);

  useEffect(() => {
    if (journey?.flow?.flowId && !Object.keys(config).length) {
      getFlow();
    }
  }, [journey?.flow?.flowId, getFlow]);

  useEffect(() => {
    if (extractedJourneyID && sessionId && !journey) {
      // Only call getJourney if the extractedJourneyID matches the current journeyId
      // and ensure we have a valid session for this specific journey
      const storedJourneyId = sessionStorage.getItem('jId');
      if (extractedJourneyID === journeyId && extractedJourneyID === storedJourneyId) {
        getJourney(extractedJourneyID);
      }
    }
  }, [extractedJourneyID, sessionId, journey, getJourney, isMobile, journeyId]);

  useEffect(() => {
    if (isInvalidJourneyId || isDirectKycJourney) {
      if(sessionStorage.getItem("sessionId")) sessionStorage.removeItem("sessionId");
      if(sessionStorage.removeItem("jId")) sessionStorage.removeItem("jId");
      if(sessionStorage.removeItem("branding")) sessionStorage.removeItem("branding");
      if(sessionStorage.removeItem("currentStep")) sessionStorage.removeItem("currentStep");
    }
  }, [isInvalidJourneyId,isDirectKycJourney]);

  // Handle post-reload loading state
  useEffect(() => {
    // Check if this is a post-reload state (after tab visibility refresh)
    const isPostReload = sessionStorage.getItem('isPostReload');

    if (isPostReload === 'true') {
      console.log('[Settings] Post-reload detected, starting 2-second timer...');
      setIsPostReloadLoading(true);

      // Clear the flag
      sessionStorage.removeItem('isPostReload');
    }

    // If we're in loading state, start the timer (regardless of flag)
    if (isPostReloadLoading) {
      console.log('[Settings] Starting 2-second timer for post-reload loading...');

      const timer = setTimeout(() => {
        console.log('[Settings] Post-reload loading complete, showing camera components...');
        setIsPostReloadLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);

  const renderJourneyContent = () => {
    // Show loader during post-reload loading state
    if (isPostReloadLoading) {
      return <Loader />;
    }
    
    if (journeyDone || sessionExpired || journeyExpired  || (isMobile && mobileSessionDone) || isInvalidJourneyId || isDirectKycJourney || journeyTerminated) {
      return (
        <ErrorBoundary>
          <div
            className="md:h-auto h-[100%] w-full sm:h-full flex flex-row justify-center md:items-center"
            style={{ backgroundColor: "#F4F4F4" }}
          >
            <div
              className="rounded-[16px] w-[900px] md:h-[620px] h-[100%] flex flex-col md:pl-[24px] md:pr-[24px] md:pt-[10px] md:pb-[10px] md:gap-[10px]"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              {isInvalidJourneyId ? (
                <JourneyStopped reason="invalid" />
              ): isDirectKycJourney ? (
                <JourneyStopped reason="directKyc" />
              ): journeyDone ? (
                <VerificationComplete calledOnRefreash={true}/>
              ) : journeyTerminated ? (
                <VerificationTerminated />
              ) : sessionExpired ? (
                <Expired isSessionExpired={true} />
              ) : journeyExpired ? (
                <Expired isJourneyExpired={true} />
              ) : isMobile && mobileSessionDone ? (
                <JourneyStopped reason="mobileAttemptsDone" />
              ) : null}
            </div>
          </div>
        </ErrorBoundary>
      );
    } else {
      return ((Object.keys(config).length && Object.keys(journey).length && routesAndNavigationLoaded && routesAndNavigationLoadedRef.current) || (sessionStorage.getItem('error'))) ? (
        <Base
          journeyId={journeyId}
          routesVersion={JSON.stringify(config.routes)}
          mobileSessionDone={mobileSessionDone}
          setMobileSessionDone={setMobileSessionDone}
          currentStepFromBackend={currentStepFromBackend}
          sessionExpired={sessionExpired}
          documentFailureReason={documentFailureReason}
          setSessionExpired={setSessionExpired}
          errorType={errorType}
          currentJourneyState={currentJourneyState}
          setErrorType={setErrorType}
          journeyExpired={journeyExpired}
          journeyInitialized={journeyInitialized}
          setJourneyInitialized={setJourneyInitialized}
          isFirstDocumentVerificationDone={isFirstDocumentVerificationDone}
          setIsFirstDocumentVerificationDone={setIsFirstDocumentVerificationDone}
          unableToProcessStepNumber={unableToProcessStepNumber}
          setUnableToProcessStepNumber={setUnableToProcessStepNumber}
          firstVerifiedDocumentName={firstVerifiedDocumentName}
          setFirstVerifiedDocumentName={setFirstVerifiedDocumentName}
          flowRegion={flowRegion}
        />
      ) : (
        <Loader />
      );
    }
  };


  return (
    <GlobalConfig.Provider value={{ config, setConfig, routesAndNavigation }}>
      <JourneyConfig.Provider
        value={{ journey, setJourney, getJourney, updateJourney, isInvalidJourneyId }}
      >
        {renderJourneyContent()}
      </JourneyConfig.Provider>
    </GlobalConfig.Provider>
  );

};

export default Settings;
